import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { FiAlertTriangle, FiCheckCircle, FiFileText } from 'react-icons/fi'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import { getFacilitiesExportExcel, ReportEmailExcel } from 'services/fund'

/**
 * «گزارش کامل صندوق» — the whole history of the fund as one Excel file, built
 * by a background task and sent by email.
 *
 * This used to be a menu modal that opened a second email modal, and the only
 * thing a manager got after «تایید» was a toast saying the report HAD BEEN
 * emailed — while it was still being built. Nothing said how long it takes,
 * whether to keep the page open, or why nothing came. So here the request is
 * one step and the answer stays on screen: where it goes, roughly when, and
 * how many reports are left. A refusal is said in place, not as a toast.
 */

const faDigits = (value: number | string) =>
    String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Failure = 'no_quota' | 'bad_email' | 'other'

const failureOf = (error: any): Failure => {
    const message: string = error?.response?.data?.message || ''
    if (message.startsWith('no remained report count')) return 'no_quota'
    if (
        message.startsWith('email validation error') ||
        message.startsWith('no email address')
    )
        return 'bad_email'
    return 'other'
}

const remainingOf = (facility?: IReportFacilitiesExcel) =>
    facility
        ? (facility.interval_full_report_count || 0) +
          (facility.purchased_full_report_count || 0)
        : undefined

const savedEmail = () => {
    try {
        return JSON.parse(localStorage.getItem('profile') || 'null')?.email || ''
    } catch {
        return ''
    }
}

interface IFundReportModalProps {
    show: boolean
    onHide: () => void
    moneyPool: string | undefined
}

export default function FundReportModal({
    show,
    onHide,
    moneyPool,
}: IFundReportModalProps) {
    const queryClient = useQueryClient()
    const [email, setEmail] = useState<string>(savedEmail)
    const [touched, setTouched] = useState(false)
    const [sentTo, setSentTo] = useState<string>()
    const [failure, setFailure] = useState<Failure>()

    const quota = useQuery(
        ['fundReportQuota', moneyPool],
        () => getFacilitiesExportExcel(moneyPool),
        { enabled: show && !!moneyPool, retry: 1 }
    )
    const remaining = remainingOf(quota.data)

    const request = useMutation(
        (address: string) => ReportEmailExcel({ moneyPool, email: address }),
        {
            onSuccess: (_, address) => {
                setSentTo(address)
                posthog.capture('fund_report_requested', {
                    fund_id: moneyPool,
                    remaining: remaining === undefined ? null : remaining - 1,
                })
                queryClient.invalidateQueries(['fundReportQuota', moneyPool])
            },
            onError: (error: any) => {
                const reason = failureOf(error)
                setFailure(reason)
                posthog.capture('fund_report_request_failed', {
                    fund_id: moneyPool,
                    reason,
                    status: error?.response?.status ?? null,
                })
                if (reason === 'no_quota')
                    queryClient.invalidateQueries([
                        'fundReportQuota',
                        moneyPool,
                    ])
            },
        }
    )

    // a reopened modal starts over at the form
    useEffect(() => {
        if (!show) {
            setSentTo(undefined)
            setFailure(undefined)
            setTouched(false)
            request.reset()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    const busy = request.isLoading
    const emailOk = EMAIL.test(email.trim())
    const exhausted = failure === 'no_quota' || remaining === 0
    const close = () => {
        if (!busy) onHide()
    }
    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        setTouched(true)
        setFailure(undefined)
        if (!emailOk || busy || exhausted) return
        request.mutate(email.trim())
    }

    return (
        <Modal show={show} onHide={close} size="sm" centered>
            <div dir="rtl" className="px-4 py-5 text-xs text-text-body">
                {sentTo ? (
                    <div className="flex flex-col items-center text-center">
                        <FiCheckCircle className="text-4xl text-brand mb-2" />
                        <p className="text-sm font-bold text-heading mb-2">
                            درخواستتان ثبت شد
                        </p>
                        <p className="leading-6">
                            گزارش در حال ساخته شدن است و معمولاً تا چند دقیقهٔ
                            دیگر به این ایمیل می‌رسد:
                        </p>
                        <bdi
                            dir="ltr"
                            className="block my-2 font-bold text-heading break-all"
                        >
                            {sentTo}
                        </bdi>
                        <ul className="w-full text-right list-disc pr-4 space-y-1 leading-6 text-text-secondary">
                            <li>
                                لازم نیست این صفحه را باز نگه دارید؛ می‌توانید
                                از همیان بیرون بروید.
                            </li>
                            <li>
                                اگر در صندوق ورودی نبود، پوشهٔ «هرزنامه» (Spam)
                                را هم نگاه کنید.
                            </li>
                            <li>
                                {quota.isFetching ? (
                                    'تعداد گزارش‌های باقی‌مانده…'
                                ) : remaining !== undefined ? (
                                    <>
                                        در این دوره {faDigits(remaining)} گزارش
                                        دیگر می‌توانید بگیرید.
                                    </>
                                ) : null}
                            </li>
                        </ul>
                        <button
                            type="button"
                            onClick={onHide}
                            className="w-full mt-4 rounded-md text-white bg-green-1 py-3"
                        >
                            متوجه شدم
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} noValidate>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 rounded-2xl bg-brand-soft text-brand-strong grid place-items-center text-2xl mb-2">
                                <FiFileText />
                            </div>
                            <p className="text-sm font-bold text-heading">
                                گزارش کامل صندوق
                            </p>
                            <p className="mt-1 leading-6 text-text-secondary">
                                همهٔ اطلاعات مالی صندوق، از روز اول تا امروز، در
                                یک فایل اکسل
                            </p>
                        </div>

                        <ol className="my-4 space-y-2">
                            {[
                                'ایمیلتان را وارد کنید و «ارسال گزارش» را بزنید.',
                                'گزارش ساخته می‌شود؛ معمولاً چند دقیقه طول می‌کشد.',
                                'فایل اکسل به ایمیلتان می‌رسد. لازم نیست صفحه باز بماند.',
                            ].map((text, i) => (
                                <li key={i} className="flex gap-2 items-start">
                                    <span className="flex-none w-5 h-5 rounded-full bg-brand-soft text-brand-deep font-bold grid place-items-center">
                                        {faDigits(i + 1)}
                                    </span>
                                    <span className="leading-5">{text}</span>
                                </li>
                            ))}
                        </ol>

                        <label
                            htmlFor="fund-report-email"
                            className="block mb-1 text-text-secondary"
                        >
                            ایمیل
                        </label>
                        <input
                            id="fund-report-email"
                            type="email"
                            dir="ltr"
                            inputMode="email"
                            autoComplete="email"
                            placeholder="name@example.com"
                            value={email}
                            disabled={busy}
                            onChange={(e) => {
                                setEmail(e.target.value)
                                if (failure === 'bad_email')
                                    setFailure(undefined)
                            }}
                            onBlur={() => setTouched(true)}
                            className="w-full outline-0 text-center border border-stroke py-3 rounded-md text-sm"
                        />
                        {((touched && !emailOk) || failure === 'bad_email') && (
                            <p className="mt-1 text-error-3">
                                ایمیل درست نیست؛ مثلاً name@example.com
                            </p>
                        )}

                        <div className="mt-3 min-h-[1.5rem] text-text-secondary">
                            {quota.isLoading ? (
                                <span className="text-brand">
                                    <DotsLoading />
                                </span>
                            ) : exhausted ? (
                                <div className="flex gap-2 items-start rounded-lg bg-warning-soft text-warning-on p-3 leading-5">
                                    <FiAlertTriangle className="flex-none mt-0.5" />
                                    <span>
                                        گزارش‌های این دورهٔ صندوق تمام شده است.
                                        در دورهٔ بعدی صندوق دوباره می‌توانید
                                        گزارش بگیرید.
                                    </span>
                                </div>
                            ) : remaining !== undefined ? (
                                <>
                                    در این دوره {faDigits(remaining)} گزارش
                                    دیگر می‌توانید بگیرید.
                                </>
                            ) : null}
                        </div>

                        {failure === 'other' && (
                            <p className="mt-2 text-error-3">
                                درخواست ثبت نشد؛ دوباره امتحان کنید.
                            </p>
                        )}

                        <div className="flex gap-2 mt-4">
                            <button
                                type="submit"
                                disabled={busy || exhausted}
                                className="flex-1 rounded-md text-white bg-green-1 py-3 disabled:opacity-50"
                            >
                                {busy ? (
                                    <DotsLoading extraStyleCircle="!bg-white" />
                                ) : (
                                    'ارسال گزارش'
                                )}
                            </button>
                            <button
                                type="button"
                                disabled={busy}
                                onClick={close}
                                className="flex-1 rounded-md bg-white border border-gray-4 py-3 disabled:opacity-50"
                            >
                                بازگشت
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Modal>
    )
}
